import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  DialogActions,
  CircularProgress,
  TextField,
  Switch,
  FormControlLabel,
} from "@material-ui/core";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: { display: "flex", flexWrap: "wrap" },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": { marginRight: theme.spacing(1) },
  },
  btnWrapper: { position: "relative" },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const Schema = Yup.object().shape({
  name: Yup.string().min(2).required(),
  address: Yup.string().required(),
  dialect: Yup.string().required(),
  timeZone: Yup.string().required(),
  port: Yup.string().required(),
});

const DbServerModal = ({ open, onClose, dbServerId }) => {
  const classes = useStyles();

  const initialState = {
    name: "",
    address: "",
    dialect: "mysql",
    timeZone: "-03:00",
    port: "3306",
    enabled: true,
    status: true,
  };

  const [dbServer, setDbServer] = useState(initialState);

  useEffect(() => {
    const fetchData = async () => {
      if (!dbServerId) return;
      try {
        const { data } = await api.get(`/dbServers/${dbServerId}`);
        setDbServer(data);
      } catch (err) {
        toastError(err);
      }
    };
    fetchData();
  }, [dbServerId]);

  const handleSave = async values => {
    try {
      if (dbServerId) {
        await api.put(`/dbServers/${dbServerId}`, values);
      } else {
        await api.post("/dbServers", values);
      }
      toast.success(i18n.t("global.success"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  const handleClose = () => {
    onClose();
    setDbServer(initialState);
  };

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle>{dbServerId ? "Editar DB Server" : "Adicionar DB Server"}</DialogTitle>
        <Formik
          initialValues={dbServer}
          enableReinitialize
          validationSchema={Schema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSave(values);
              actions.setSubmitting(false);
            }, 300);
          }}
        >
          {({ values, touched, errors, isSubmitting }) => (
            <Form>
              <DialogContent dividers>
                <Field as={TextField} fullWidth label="Nome" name="name" variant="outlined" margin="dense" error={touched.name && Boolean(errors.name)} helperText={touched.name && errors.name} />
                <Field as={TextField} fullWidth label="Endereço" name="address" variant="outlined" margin="dense" error={touched.address && Boolean(errors.address)} helperText={touched.address && errors.address} />
                <div className={classes.multFieldLine}>
                  <Field as={TextField} fullWidth label="Dialeto" name="dialect" variant="outlined" margin="dense" />
                  <Field as={TextField} fullWidth label="Timezone" name="timeZone" variant="outlined" margin="dense" />
                </div>
                <Field as={TextField} fullWidth label="Porta" name="port" variant="outlined" margin="dense" error={touched.port && Boolean(errors.port)} helperText={touched.port && errors.port} />
                <div className={classes.multFieldLine}>
                  <FormControlLabel
                    control={<Field as={Switch} color="primary" name="enabled" checked={values.enabled} />}
                    label="Enabled"
                  />
                  <FormControlLabel
                    control={<Field as={Switch} color="primary" name="status" checked={values.status} />}
                    label="Status"
                  />
                </div>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose} color="secondary" disabled={isSubmitting} variant="outlined">
                  {i18n.t("global.buttons.cancel")}
                </Button>
                <Button type="submit" color="primary" disabled={isSubmitting} variant="contained" className={classes.btnWrapper}>
                  {dbServerId ? i18n.t("global.buttons.okEdit") : i18n.t("global.buttons.okAdd")}
                  {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default React.memo(DbServerModal);
